"use client";

import {
  Checkbox,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT } from "@/components/tool-kit/styles";
import type { SpiderQuestionGeneratorStrings } from "@/components/tools/spider-question-generator";
import { t } from "@/lib/i18n/interpolate";
import type {
  ElementInput,
  Framework,
  FrameworkInput,
} from "@/lib/tool-kit/research-discovery/question-frameworks";

interface ElementText {
  letter: string;
  name: string;
  prompt: string;
  example: string;
}

export function elementText(
  strings: SpiderQuestionGeneratorStrings,
  framework: Framework,
  key: string
): ElementText {
  const table = strings.elements as Record<string, Record<string, ElementText>>;
  return (
    table[framework.id]?.[key] ?? {
      letter: key,
      name: key,
      prompt: "",
      example: "",
    }
  );
}

/** One card per element: the words for the question, the search terms, and whether to search it. */
export function FrameworkElements({
  framework,
  input,
  strings,
  onChange,
}: {
  framework: Framework;
  input: FrameworkInput;
  strings: SpiderQuestionGeneratorStrings;
  onChange: (key: string, next: ElementInput) => void;
}) {
  return (
    <div className="space-y-4">
      {framework.keys.map((key) => {
        const text = elementText(strings, framework, key);
        const current = input[key];
        const id = `sqg-${framework.id}-${key}`;
        return (
          <div className={`${CARD} space-y-3`} key={key}>
            <TextField
              hint={text.prompt}
              id={id}
              label={`${text.letter} — ${text.name}`}
              onChange={(value) => onChange(key, { ...current, value })}
              placeholder={text.example}
              value={current.value}
            />
            <TextAreaField
              hint={strings.termsHint}
              id={`${id}-terms`}
              label={t(strings.termsLabel, { element: text.name })}
              onChange={(terms) => onChange(key, { ...current, terms })}
              placeholder={strings.termsPlaceholder}
              rows={2}
              value={current.terms}
            />
            <Checkbox
              checked={current.include}
              label={strings.includeLabel}
              onChange={(include) => onChange(key, { ...current, include })}
            />
            {framework.optional.includes(key) ? (
              <p className={HINT}>{strings.optionalNote}</p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
